import React from "react";
import classNames from "classnames";
import DOMPurify from "dompurify";
import { ColumnsBlockItem } from "../../types/optimizely/blocks";
import {
  getColorClasses,
  getTextColorClass,
} from "../../utils/getBackgroundColor";
import { ColorPalette } from "../../types/background";
import Link from "../Link/Link";
import { TeaserCardProps } from "../TeaserCard/TeaserCard";
import { sanitizeOrPassthrough } from "../../helpers/rteHelpers";

interface ColumnContentRowItemBlockProps {
  item: ColumnsBlockItem;
  imageHeightClass: string;
}

const localPurifier = DOMPurify();

export const getButtonType = (
  backgroundColor: TeaserCardProps["backgroundColor"]
) => {
  switch (backgroundColor) {
    case "white":
    case "secondaryYellow":
    case "primaryBlue":
    case "secondaryGreen":
    case "secondaryLightGreen":
      return "link-secondary";
    case "primary":
    case "primaryTurquoise":
    case "secondaryDarkGreen":
      return "link-secondary-reverse";
  }
};

export const ColumnContentRowItemBlock: React.FC<
  ColumnContentRowItemBlockProps
> = ({ item, imageHeightClass }) => {
  const title = item.title?.value;
  const content = item.subtitle?.value;
  const links = item.linkList?.value || [];
  const icon = item.iconImage?.expandedValue;
  const imageThumbnail = item.itemImage?.expandedValue;
  const backgroundColor = item.backgroundColor?.value as ColorPalette;

  const backgroundClasses = getColorClasses(backgroundColor);
  const textColorClass = getTextColorClass(backgroundColor);

  return (
    <div
      className={classNames(
        "flex flex-col rounded-lg h-full",
        backgroundClasses
      )}
    >
      {!icon && imageThumbnail && (
        <img
          loading="lazy"
          decoding="async"
          src={imageThumbnail.url}
          alt={title}
          className={classNames(
            "w-100 object-cover rounded-t",
            imageHeightClass,
            backgroundColor === "transparent" && "pb-5"
          )}
        />
      )}
      <div
        className={classNames(backgroundColor !== "transparent" && "py-5 px-6")}
      >
        {icon && <img src={icon.url} alt={title} className="mb-2 h-[50px]" />}
        <h3 className={classNames("text-2xl mb-[10px]", textColorClass)}>
          {title}
        </h3>
        <p
          className={classNames("text-lg", textColorClass)}
          dangerouslySetInnerHTML={{ __html: sanitizeOrPassthrough(localPurifier, content) }}
        />
        {links.length > 0 && (
          <div className="mt-4">
            {links.map((link: { text?: string; href?: string }) => (
              <Link
                text={link?.text}
                variant={"link-secondary"}
                className={classNames(
                  "text-left",
                  "cursor-pointer",
                  "justify-start",
                  textColorClass
                )}
                href={link?.href}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ColumnContentRowItemBlock;
